/**
 * OOXML numbering → Office.js List API translation.
 *
 * `newList` forks a brand-new Word list, and `startNewList()` returns a list with
 * NO level definitions — every level renders with Word's default marker until we
 * define it. The backend ships the SOURCE list's scheme (`listChange.numberFormat`,
 * read straight out of `numbering.xml`) because Office.js cannot read an existing
 * list's `w:numFmt` / `w:lvlText` on Word Online: `ListLevel` is WordApiDesktop-only.
 * This module turns those raw OOXML values into the arguments
 * `List.setLevelNumbering` / `List.setLevelBullet` expect.
 *
 * Deliberately free of any `Word` global so it unit-tests without an Office
 * runtime: it returns ENUM KEYS (`"arabic"`, `"solid"`) that the caller indexes
 * into `Word.ListNumbering` / `Word.ListBullet`.
 */

import { NumberFormatLevel } from "../../../interfaces/autoRedlineTypes";

/** Keys of `Word.ListNumbering` (WordApi 1.3 — only six values exist). */
export type ListNumberingKey =
  | "arabic"
  | "upperRoman"
  | "lowerRoman"
  | "upperLetter"
  | "lowerLetter";

/** Keys of `Word.ListBullet` we map onto. */
export type ListBulletKey = "solid" | "hollow" | "square" | "diamonds" | "arrow" | "checkmark";

/** A level resolved to the Office.js call it needs. */
export type ResolvedLevelFormat =
  | {
      level: number;
      kind: "number";
      numbering: ListNumberingKey;
      formatString: Array<string | number>;
    }
  | { level: number; kind: "bullet"; bullet: ListBulletKey };

/** Word supports list levels 0-8. */
const MAX_LEVEL = 8;

/**
 * OOXML `w:numFmt` → `Word.ListNumbering` key.
 *
 * `Word.ListNumbering` has only six members, whereas `w:numFmt` has dozens.
 * Anything absent here (`ordinal`, `cardinalText`, `decimalZero`, `legal`, the
 * locale styles) is only reachable through `ListLevel.numberStyle`, which does
 * not exist on Word Online — those degrade to `arabic` via `DEFAULT_NUMBERING`.
 */
const NUMBERING_BY_NUM_FMT: Record<string, ListNumberingKey> = {
  decimal: "arabic",
  upperRoman: "upperRoman",
  lowerRoman: "lowerRoman",
  upperLetter: "upperLetter",
  lowerLetter: "lowerLetter",
};

const DEFAULT_NUMBERING: ListNumberingKey = "arabic";

/**
 * Bullet glyph (the `w:lvlText` of a bullet level) → `Word.ListBullet` key.
 * Word writes these as private-use codepoints in the Symbol / Wingdings fonts;
 * the plain-Unicode equivalents are listed too since some producers emit those.
 */
const BULLET_BY_GLYPH: Record<string, ListBulletKey> = {
  "\uF0B7": "solid", // Symbol bullet
  "\u2022": "solid", // •
  o: "hollow", // Courier New "o"
  "\u25E6": "hollow", // ◦
  "\uF0A7": "square", // Wingdings square
  "\u25AA": "square", // ▪
  "\uF0D8": "arrow", // Wingdings arrowhead
  "\u27A2": "arrow", // ➢
  "\uF0FC": "checkmark", // Wingdings checkmark
  "\u2713": "checkmark", // ✓
  "\uF076": "diamonds", // Wingdings diamond
  "\u2666": "diamonds", // ♦
};

const DEFAULT_BULLET: ListBulletKey = "solid";

/**
 * Parse an OOXML `w:lvlText` template into the array form
 * `List.setLevelNumbering` takes.
 *
 * `%1`-`%9` are 1-based level references; `setLevelNumbering` wants 0-based
 * integers, with literal text as strings. So `"%1.%2"` → `[0, ".", 1]` and
 * `"(%1)"` → `["(", 0, ")"]`. A bare `%` not followed by 1-9 is literal.
 *
 * @param lvlText Raw `w:lvlText` value
 * @returns Mixed literal/level-index array, empty when there is nothing to render
 */
export function parseLevelText(lvlText: string): Array<string | number> {
  const parts: Array<string | number> = [];
  let literal = "";

  const flush = () => {
    if (literal) {
      parts.push(literal);
      literal = "";
    }
  };

  for (let i = 0; i < lvlText.length; i++) {
    const char = lvlText[i];
    const next = lvlText[i + 1];
    if (char === "%" && next >= "1" && next <= "9") {
      flush();
      parts.push(Number(next) - 1);
      i++; // consume the digit
    } else {
      literal += char;
    }
  }
  flush();

  return parts;
}

/**
 * Resolve one backend level definition to the Office.js call it needs.
 *
 * @param entry Level definition from `listChange.numberFormat`
 * @returns The resolved call, or null when the level is out of Word's 0-8 range
 *   or explicitly unnumbered (`w:numFmt` = "none")
 */
export function resolveLevelFormat(entry: NumberFormatLevel): ResolvedLevelFormat | null {
  const level = Math.trunc(entry.level);
  if (!Number.isFinite(level) || level < 0 || level > MAX_LEVEL) return null;
  if (entry.numFmt === "none") return null;

  if (entry.numFmt === "bullet") {
    return { level, kind: "bullet", bullet: BULLET_BY_GLYPH[entry.lvlText] ?? DEFAULT_BULLET };
  }

  // A numbered level whose template carries no %n placeholder would render a
  // fixed marker on every item; fall back to this level's own counter + a dot,
  // which is also what an empty lvlText gets.
  const parsed = parseLevelText(entry.lvlText ?? "");
  const formatString = parsed.some((part) => typeof part === "number") ? parsed : [level, "."];

  return {
    level,
    kind: "number",
    numbering: NUMBERING_BY_NUM_FMT[entry.numFmt] ?? DEFAULT_NUMBERING,
    formatString,
  };
}

/**
 * Resolve the whole scheme to apply to a freshly forked list.
 *
 * Every level the source list defined is emitted, not just the one being
 * targeted: a follow-up `continueList` can attach siblings to this fork at any
 * level, and an undefined level renders as a bullet.
 *
 * @param numberFormat Backend scheme; null/absent when the source was unnumbered
 * @param targetLevel The level the forked paragraph will land on
 * @returns Resolved per-level calls. When no usable scheme was supplied, a plain
 *   decimal fallback covering levels 0..targetLevel.
 */
export function resolveNumberFormat(
  numberFormat: NumberFormatLevel[] | null | undefined,
  targetLevel: number
): ResolvedLevelFormat[] {
  const resolved = (numberFormat ?? [])
    .map(resolveLevelFormat)
    .filter((entry): entry is ResolvedLevelFormat => entry !== null);

  if (resolved.length > 0) return resolved;

  return decimalFallback(targetLevel);
}

/**
 * Plain decimal scheme for levels 0..targetLevel, each rendering its own counter
 * ("1.", "1.", …). Used when the source paragraph carried no numbering, so there
 * is no scheme to inherit — levels below the target are seeded too so a later
 * `continueList` onto this list still renders.
 */
function decimalFallback(targetLevel: number): ResolvedLevelFormat[] {
  const top = Math.min(MAX_LEVEL, Math.max(0, Math.trunc(targetLevel) || 0));
  const levels: ResolvedLevelFormat[] = [];
  for (let level = 0; level <= top; level++) {
    levels.push({
      level,
      kind: "number",
      numbering: DEFAULT_NUMBERING,
      formatString: [level, "."],
    });
  }
  return levels;
}
